import { Home, Calendar, MapPin, ExternalLink, Radio, Pencil, Sparkles } from "lucide-react";

const fmtUSD = (n) =>
  n == null ? "—" : "$" + Math.round(Number(n)).toLocaleString();
const fmtNumber = (n) =>
  n == null || n === "" ? "—" : Number(n).toLocaleString();
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const fmtDistance = (miles) =>
  miles == null || miles === "" ? "—" :
    Number(miles) < 0.1 ? "<0.1 mi" : `${Number(miles).toFixed(2)} mi`;

function normalizeComp(raw) {
  // RentCast comps use camelCase + investor-entered manual comps use snake_case.
  const price = Number(raw.sale_price ?? raw.salePrice ?? raw.price ?? 0) || null;
  const sqft = Number(raw.square_footage ?? raw.squareFootage ?? raw.sqft ?? 0) || null;
  const ppsf = price && sqft ? Math.round(price / sqft) : null;
  const address = raw.address ?? raw.formattedAddress ?? "—";
  const bedrooms = raw.bedrooms ?? raw.beds ?? null;
  const bathrooms = raw.bathrooms ?? raw.baths ?? null;
  const yearBuilt = raw.yearBuilt ?? raw.year_built ?? null;
  const distance = raw.distance ?? null;
  const lastSaleDate = raw.lastSaleDate ?? raw.last_sale_date ?? raw.listedDate ?? null;
  const propertyType = raw.propertyType ?? raw.property_type ?? null;
  // A manual comp is one the investor entered: only core 3 fields. If extras
  // exist (bed/bath/yearBuilt/distance/lastSaleDate) it came from RentCast.
  const isRentCast = !!(bedrooms || bathrooms || yearBuilt || distance || lastSaleDate);
  return {
    address, price, sqft, ppsf, bedrooms, bathrooms, yearBuilt,
    distance, lastSaleDate, propertyType,
    source: isRentCast ? "rentcast" : "manual",
  };
}

/**
 * Sales-comparable table for the valuation result. Blends RentCast-pulled
 * comparables with investor-entered manual comps in the same grid and
 * flags each row's source. Rows sort newest → oldest sale when available,
 * otherwise by closest distance, and always show price, sqft, $/sqft.
 *
 * Highlights the subject property's own $/sqft via a dashed line so users
 * can eyeball whether their projected value sits above / below comps.
 */
export default function ValuationCompsTable({
  comps, subjectSqft, subjectPricePerSqft, compAveragePpsf,
}) {
  if (!Array.isArray(comps) || comps.length === 0) {
    return (
      <div className="bg-white border border-dashed border-slate-300 rounded-sm p-8 text-center text-sm text-slate-500" data-testid="valuation-comps-empty">
        <Home className="w-7 h-7 text-slate-300 mx-auto mb-2" />
        <div className="font-semibold text-slate-700">No comparables yet</div>
        <div className="text-xs mt-1 max-w-sm mx-auto">
          Enable RentCast (Settings → Integrations) for an auto-pulled pool of recent sales,
          or add nearby sales manually from the form above.
        </div>
      </div>
    );
  }

  const normalized = comps.map(normalizeComp).sort((a, b) => {
    // Prefer most-recent sale; fall back to nearest distance.
    const at = a.lastSaleDate ? new Date(a.lastSaleDate).getTime() : 0;
    const bt = b.lastSaleDate ? new Date(b.lastSaleDate).getTime() : 0;
    if (at || bt) return bt - at;
    const ad = a.distance ?? 999, bd = b.distance ?? 999;
    return ad - bd;
  });

  const rentcastCount = normalized.filter((c) => c.source === "rentcast").length;
  const manualCount = normalized.length - rentcastCount;

  // For the PPSF chip scale, find min/max so we can render a mini-bar.
  const ppsfValues = normalized.map((c) => c.ppsf).filter((v) => v != null);
  const ppsfMax = ppsfValues.length ? Math.max(...ppsfValues) : 1;

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="valuation-comps-table">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Home className="w-4 h-4 text-slate-500" />
          <div>
            <div className="overline text-slate-500">Comparables</div>
            <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>
              {normalized.length} recent sale{normalized.length === 1 ? "" : "s"} used in this valuation
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] font-mono">
          {rentcastCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">
              <Radio className="w-3 h-3" /> {rentcastCount} RentCast
            </span>
          )}
          {manualCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200">
              <Pencil className="w-3 h-3" /> {manualCount} manual
            </span>
          )}
          {compAveragePpsf != null && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-slate-900 text-white">
              <Sparkles className="w-3 h-3" /> avg ${compAveragePpsf.toLocaleString()}/sqft
            </span>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <Th>Address</Th>
              <Th className="text-right">Sale price</Th>
              <Th className="text-right">Sqft</Th>
              <Th className="text-right">$/sqft</Th>
              <Th className="text-right">Bed / Bath</Th>
              <Th className="text-right">Year</Th>
              <Th className="text-right">Dist</Th>
              <Th className="text-right">Sold</Th>
            </tr>
          </thead>
          <tbody>
            {normalized.map((c, i) => (
              <CompRow key={`${c.address}-${i}`} idx={i} c={c} ppsfMax={ppsfMax} subjectPricePerSqft={subjectPricePerSqft} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-2.5 border-t border-slate-100 bg-slate-50/80 text-[11px] text-slate-500 font-mono flex items-center gap-2 justify-between flex-wrap">
        <span>
          Subject property sqft: <b>{fmtNumber(subjectSqft)}</b>
          {subjectPricePerSqft != null && (
            <> · implied <b className="text-slate-900">${subjectPricePerSqft.toLocaleString()}/sqft</b></>
          )}
        </span>
        {rentcastCount > 0 && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Data via <a className="underline hover:text-slate-900" href="https://rentcast.io" target="_blank" rel="noreferrer">RentCast.io <ExternalLink className="w-2.5 h-2.5 inline" /></a>
          </span>
        )}
      </div>
    </div>
  );
}

function CompRow({ idx, c, ppsfMax, subjectPricePerSqft }) {
  const pct = c.ppsf && ppsfMax ? Math.max(6, (c.ppsf / ppsfMax) * 100) : 0;
  // Colour the price-per-sqft bar relative to the subject property's implied $/sqft:
  // green when the comp's ppsf is at or below our implied (comp supports our value),
  // amber when slightly above, red when comp is materially higher.
  let barColor = "bg-slate-400";
  if (subjectPricePerSqft && c.ppsf) {
    const delta = c.ppsf / subjectPricePerSqft;
    barColor = delta <= 1.05 ? "bg-emerald-500"
             : delta <= 1.20 ? "bg-amber-500"
             : "bg-rose-500";
  }
  return (
    <tr className={`border-b border-slate-100 hover:bg-slate-50/70 transition-colors ${idx % 2 === 1 ? "bg-slate-50/30" : ""}`} data-testid={`comp-row-${idx}`}>
      <Td>
        <div className="flex items-center gap-2 min-w-0">
          {c.source === "rentcast"
            ? <Radio className="w-3 h-3 text-emerald-600 shrink-0" title="Pulled from RentCast" />
            : <Pencil className="w-3 h-3 text-indigo-600 shrink-0" title="Manually entered" />
          }
          <span className="truncate" title={c.address}>{c.address}</span>
        </div>
        {c.propertyType && (
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-0.5">
            {c.propertyType}
          </div>
        )}
      </Td>
      <Td className="text-right font-mono font-semibold text-slate-900">{fmtUSD(c.price)}</Td>
      <Td className="text-right font-mono">{fmtNumber(c.sqft)}</Td>
      <Td className="text-right font-mono">
        <div className="flex items-center justify-end gap-2 min-w-[84px]">
          <div className="flex-1 h-1.5 bg-slate-100 rounded-sm overflow-hidden hidden md:block">
            <div className={`h-full ${barColor} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
          </div>
          <span className="font-bold text-slate-900">{c.ppsf ? `$${c.ppsf.toLocaleString()}` : "—"}</span>
        </div>
      </Td>
      <Td className="text-right font-mono text-slate-600">
        {c.bedrooms ?? "—"} / {c.bathrooms ?? "—"}
      </Td>
      <Td className="text-right font-mono text-slate-600">{c.yearBuilt ?? "—"}</Td>
      <Td className="text-right font-mono text-slate-600">{fmtDistance(c.distance)}</Td>
      <Td className="text-right font-mono text-slate-500 text-xs">
        <Calendar className="w-3 h-3 inline text-slate-300 mr-1" />
        {fmtDate(c.lastSaleDate)}
      </Td>
    </tr>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-2.5 overline text-slate-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}
